import React from 'react';
import { CircleInfoIcon } from '@revfluence/fresh-icons/regular/esm';
import { ChartLineIcon, ChartBarIcon } from '@revfluence/fresh-icons/regular/esm';
import { BadgeDollarIcon, SackDollarIcon, CartShoppingIcon } from '@revfluence/fresh-icons/solid/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { P } from '@frontend/shadcn/components/typography/p';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@frontend/shadcn/components/ui/card';
import { Period, SocialAdsAnalytics_QueryFragment } from '@frontend/gql/social/graphql';
import { RectangleAdIcon } from '@revfluence/fresh-icons/regular/esm';
import { DateType } from '../Analytics/types';

// Component imports
import TotalSpendCard from './components/ad-highlight-cards/total-spend-card';
import SalesGeneratedCard from './components/ad-highlight-cards/sales-generated-card';
import RoasCard from './components/ad-highlight-cards/roas-card';
import { Impressions } from './components/ad-highlight-long-card/impressions';
import { Reach } from './components/ad-highlight-long-card/reach';
import { Clicks } from './components/ad-highlight-long-card/clicks';
import AdSpendVsSalesChart from './components/ad-spend-vs-sales-chart';
import RoasOverTimeChart from './components/roas-over-time-chart';
import { Members } from './components/ad-highlight-long-card/members';
import { Posts } from './components/ad-highlight-long-card/posts';
import { Cpc } from './components/ad-highlight-long-card/cpc';
import { Cpm } from './components/ad-highlight-long-card/cpm';
import { Cpp } from './components/ad-highlight-long-card/cpp';
import { Ctr } from './components/ad-highlight-long-card/ctr';
import { CpcBar } from './components/ad-highlight-long-card/cpc-bar';
import { CpmBar } from './components/ad-highlight-long-card/cpm-bar';
import { CppBar } from './components/ad-highlight-long-card/cpp-bar';
import { CtrBar } from './components/ad-highlight-long-card/ctr-bar';
import ImpressionsChart from './components/impressions-chart';
import ReachChart from './components/reach-chart';
import ClickThroughRateChart from './components/views-per-person-chart';
import { MetricWithBar } from './components/metric-with-bar';
import { EmptyChart, EmptyHighlightCard, EmptyLongMetric, EmptyMetricWithBar } from './components/empty-states';
import { isHighlightDataMissing, isMetricDataMissing, isChartDataMissing } from './utils';
import { SocialAlert, AlertButton } from '../components/alert';
import TotalAdsCard from './components/ad-highlight-cards/total-ads-card';

import { TopSocialAccountsTable } from './components/social-accounts/top-social-accounts-table';
import type { SocialAccountData } from './components/social-accounts-table/social-accounts-table-types';
import { SocialAdsOverviewTable } from './components/ads-table/SocialAdsOverviewTable';
import type { TopAdData } from './components/ads-table/ads-table-types';
import {
  AnalyticsCard,
  AnalyticsCardHeader,
  AnalyticsCardTitle,
  AnalyticsCardContent,
} from './components/analytics-card';

// Loading components for different sections
const HighlightCardSkeleton = () => (
  <Card className="bg-background border border-border rounded-2xl">
    <CardContent className="p-6">
      <div className="space-y-4">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-8 w-32" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const LongCardMetricsSkeleton = () => (
  <Card>
    <CardHeader />
    <CardContent>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="space-y-2 p-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-6 w-20" />
            <div className="flex items-center gap-1">
              <Skeleton className="h-3 w-3" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const ChartCardSkeleton = ({ title }: { title: string }) => (
  <AnalyticsCard>
    <AnalyticsCardHeader>
      <AnalyticsCardTitle>{title}</AnalyticsCardTitle>
    </AnalyticsCardHeader>
    <AnalyticsCardContent>
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <div className="flex justify-center gap-4">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </AnalyticsCardContent>
  </AnalyticsCard>
);

const MetricWithBarSkeleton = ({ title }: { title: string }) => (
  <AnalyticsCard>
    <AnalyticsCardHeader>
      <AnalyticsCardTitle>{title}</AnalyticsCardTitle>
    </AnalyticsCardHeader>
    <AnalyticsCardContent>
      <div className="space-y-4 flex flex-row h-32">
        {/* Metric skeleton (left) */}
        <div className="flex flex-col justify-center flex-1 space-y-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-8 w-32" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        {/* Bar chart skeleton (right) */}
        <div className="flex flex-col justify-end items-end flex-1 min-w-[120px] h-full p-2">
          <div className="flex flex-row items-end gap-1 h-32 w-[116px]" aria-busy="true" aria-label="Loading chart">
            <Skeleton className="w-10 h-8 rounded-t bg-primary/10 animate-pulse" />
            <Skeleton className="w-10 h-16 rounded-t bg-primary/10 animate-pulse" />
          </div>
        </div>
      </div>
    </AnalyticsCardContent>
  </AnalyticsCard>
);

// Main component props interface
interface SocialAdsOverviewProps {
  className?: string;
  // Data props - using the fragment-typed data from GraphQL query
  isLoading: boolean;
  currentInterval: NonNullable<SocialAdsAnalytics_QueryFragment['client']>['currentInterval'];
  previousInterval: NonNullable<SocialAdsAnalytics_QueryFragment['client']>['previousInterval'];
  currentIntervalNotAspireSourced: NonNullable<
    SocialAdsAnalytics_QueryFragment['client']
  >['currentIntervalNotAspireSourced'];
  previousIntervalNotAspireSourced: NonNullable<
    SocialAdsAnalytics_QueryFragment['client']
  >['previousIntervalNotAspireSourced'];
  currentIntervalsIntervalInsights: NonNullable<
    SocialAdsAnalytics_QueryFragment['client']
  >['currentIntervalsIntervalInsights'];
  currentIntervalsIntervalInsightsNotAspireSourced: NonNullable<
    SocialAdsAnalytics_QueryFragment['client']
  >['currentIntervalsIntervalInsightsNotAspireSourced'];
  currentIntervalRange: {
    precision: Period;
  };
  topAds: TopAdData[];
  topSocialAccounts: SocialAccountData[];
  currency: string;
  // Query variables
  queryVariables: {
    showNonAttributedData: boolean;
    fromOldData: boolean;
    startDate: string;
    endDate: string;
    dateType: DateType;
  };
  // Alert states
  hasNoMetaAdAccounts: boolean;
  hasDisconnectedMetaAdAccounts: boolean;
  // Event handlers
  onConnectInstagram: () => void;
  onReconnectAccount: () => void;
  // Helper function
  getPrecisionLabel: (precision: Period) => string;
  handleTabChange: (tab: string) => void;
}

export const SocialAdsOverview: React.FC<SocialAdsOverviewProps> = ({
  className,
  isLoading,
  currentInterval,
  previousInterval,
  currentIntervalNotAspireSourced,
  previousIntervalNotAspireSourced,
  currentIntervalsIntervalInsights,
  currentIntervalsIntervalInsightsNotAspireSourced,
  currentIntervalRange,
  topAds,
  topSocialAccounts,
  currency,
  queryVariables,
  hasNoMetaAdAccounts,
  hasDisconnectedMetaAdAccounts,
  onConnectInstagram,
  onReconnectAccount,
  getPrecisionLabel,
  handleTabChange,
}) => {
  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {/* Alerts section - only show if there are alerts to display */}
      {(hasNoMetaAdAccounts || hasDisconnectedMetaAdAccounts) && (
        <div className="flex flex-col gap-4">
          {/* Show connect alert only if no ad accounts */}
          {hasNoMetaAdAccounts && (
            <SocialAlert
              type="info"
              title="Connect your Instagram Ad Account"
              description="Analyze how well your ads are performing by connecting your Instagram Ad Account to Aspire."
              button={<AlertButton onClick={onConnectInstagram}>Connect Account</AlertButton>}
            />
          )}

          {/* Show reconnect alert only if there are disconnected accounts */}
          {hasDisconnectedMetaAdAccounts && (
            <SocialAlert
              type="error"
              title="Ad account has been disconnected"
              description="We have detected an issue with your connected Instagram Ad account. To ensure seamless operation, please reconnect your account."
              button={<AlertButton onClick={onReconnectAccount}>Reconnect Account</AlertButton>}
            />
          )}
        </div>
      )}

      {/* Ads Highlights Section */}
      <div className="mb-0">
        <H3>Ads Highlights</H3>
        <P>
          Key metrics define your success, helping you quickly assess what's working and where to dive deeper. Use it to
          identify how your content is generated through Aspire performing as Ads.
        </P>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <HighlightCardSkeleton />
            <HighlightCardSkeleton />
            <HighlightCardSkeleton />
            <HighlightCardSkeleton />
          </>
        ) : (
          <>
            {isHighlightDataMissing(currentInterval, 'totalAds') ? (
              <EmptyHighlightCard
                title="Total Ads"
                iconComponent={<RectangleAdIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                backgroundColor="bg-grey-6"
                valueType="number"
                showNonAttributedData={queryVariables.showNonAttributedData}
              />
            ) : (
              <TotalAdsCard
                currentInterval={currentInterval}
                previousInterval={previousInterval}
                currentIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                }
                previousIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                }
                showNonAttributedData={queryVariables.showNonAttributedData}
                startDate={queryVariables.startDate}
                endDate={queryVariables.endDate}
                dateType={queryVariables.dateType}
              />
            )}
            {isHighlightDataMissing(currentInterval, 'totalSpend') ? (
              <EmptyHighlightCard
                title="Ads Spend"
                iconComponent={<BadgeDollarIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                backgroundColor="bg-blue-6"
                valueType="currency"
                showNonAttributedData={queryVariables.showNonAttributedData}
              />
            ) : (
              <TotalSpendCard
                currentInterval={currentInterval}
                previousInterval={previousInterval}
                currentIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                }
                previousIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                }
                showNonAttributedData={queryVariables.showNonAttributedData}
                startDate={queryVariables.startDate}
                endDate={queryVariables.endDate}
                dateType={queryVariables.dateType}
              />
            )}
            {isHighlightDataMissing(currentInterval, 'totalPurchase') ? (
              <EmptyHighlightCard
                title="Sales Generated from Ads"
                iconComponent={<CartShoppingIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                backgroundColor="bg-teal-6"
                valueType="currency"
                showNonAttributedData={queryVariables.showNonAttributedData}
              />
            ) : (
              <SalesGeneratedCard
                currentInterval={currentInterval}
                previousInterval={previousInterval}
                currentIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                }
                previousIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                }
                showNonAttributedData={queryVariables.showNonAttributedData}
                startDate={queryVariables.startDate}
                endDate={queryVariables.endDate}
                dateType={queryVariables.dateType}
              />
            )}
            {isMetricDataMissing(currentInterval, 'totalRoas') ? (
              <EmptyHighlightCard
                title="ROAS"
                iconComponent={<SackDollarIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                backgroundColor="bg-purple-6"
                valueType="percentage"
                showNonAttributedData={queryVariables.showNonAttributedData}
              />
            ) : (
              <RoasCard
                currentInterval={currentInterval}
                previousInterval={previousInterval}
                currentIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                }
                previousIntervalNotAspireSourced={
                  queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                }
                showNonAttributedData={queryVariables.showNonAttributedData}
                startDate={queryVariables.startDate}
                endDate={queryVariables.endDate}
                dateType={queryVariables.dateType}
              />
            )}
          </>
        )}

        {/* Long metrics card */}
        <div className="col-span-1 md:col-span-2 lg:col-span-4">
          {isLoading ? (
            <LongCardMetricsSkeleton />
          ) : (
            <Card>
              <CardHeader />
              <CardContent>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                  {isMetricDataMissing(currentInterval, 'totalClicks') ? (
                    <EmptyLongMetric label="Clicks" />
                  ) : (
                    <Clicks currentInterval={currentInterval} previousInterval={previousInterval} />
                  )}
                  {isMetricDataMissing(currentInterval, 'totalReach') ? (
                    <EmptyLongMetric label="Reach" />
                  ) : (
                    <Reach currentInterval={currentInterval} previousInterval={previousInterval} />
                  )}
                  {isMetricDataMissing(currentInterval, 'totalImpressions') ? (
                    <EmptyLongMetric label="Impressions" />
                  ) : (
                    <Impressions currentInterval={currentInterval} previousInterval={previousInterval} />
                  )}
                  {isMetricDataMissing(currentInterval, 'totalCreators') ? (
                    <EmptyLongMetric label="Members" />
                  ) : (
                    <Members currentInterval={currentInterval} previousInterval={previousInterval} />
                  )}
                  {isMetricDataMissing(currentInterval, 'totalPosts') ? (
                    <EmptyLongMetric label="Posts" />
                  ) : (
                    <Posts currentInterval={currentInterval} previousInterval={previousInterval} />
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Ads Spend Overview Section */}
      <>
        <H3>Ads Spend Overview</H3>
        <P>
          Ensure that every dollar is working toward your Ads' goals. Examining how much you spend over time and the
          revenue attributable to ads through content generated through Aspire.
        </P>
      </>
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
        {isLoading ? (
          <>
            <div className="col-span-1 lg:col-span-4">
              <ChartCardSkeleton title="Ad Spend vs. Sales" />
            </div>
            <div className="col-span-1 lg:col-span-2">
              <ChartCardSkeleton title="ROAS Over Time" />
            </div>
          </>
        ) : (
          <>
            <AnalyticsCard className="col-span-1 lg:col-span-4">
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>
                  Ad Spend vs. Sales
                  <Tooltip>
                    <TooltipTrigger className="flex items-center">
                      <CircleInfoIcon className="w-4 h-4 text-grey-3 hover:text-grey-5 transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[250px]">
                      <P className="text-sm">
                        {getPrecisionLabel(currentIntervalRange.precision)} Ad Spend and Sales trends, split into
                        Aspire-attributed and non-attributed portions for comparison.
                      </P>
                    </TooltipContent>
                  </Tooltip>
                </AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                  <EmptyChart
                    title="Ad Spend or Sales Data Unavailable"
                    message="Try adjusting your filters or check back once your campaigns has gathered more results."
                    iconComponent={<ChartLineIcon className="w-12 h-12" />}
                  />
                ) : (
                  <AdSpendVsSalesChart
                    currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                    currentIntervalsIntervalInsightsNotAspireSourced={currentIntervalsIntervalInsightsNotAspireSourced}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                    precision={currentIntervalRange.precision}
                    currency={currency}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>
            <AnalyticsCard className="col-span-1 lg:col-span-2">
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>
                  ROAS Over Time
                  <Tooltip>
                    <TooltipTrigger className="flex items-center">
                      <CircleInfoIcon className="w-4 h-4 text-grey-3 hover:text-grey-5 transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[250px]">
                      <P className="text-sm">
                        Total return on ad spend (ROAS) from purchases of items shared between Brand and Retailer. This
                        number is based on information received from one or more retailers' connected Facebook Business
                        Tools. You can see the amount's breakdown is attributed to Aspire vs not attributed to Aspire.
                      </P>
                    </TooltipContent>
                  </Tooltip>
                </AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                  <EmptyChart
                    title="ROAS Data Unavailable"
                    message="Try adjusting your filters or check back once your campaigns has gathered more results."
                    iconComponent={<ChartLineIcon className="w-12 h-12" />}
                  />
                ) : (
                  <RoasOverTimeChart
                    currentIntervalsMetaInsights={currentIntervalsIntervalInsights}
                    currentIntervalsMetaInsightsNotAspireSourced={currentIntervalsIntervalInsightsNotAspireSourced}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                    precision={currentIntervalRange.precision}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>
          </>
        )}
      </div>

      {/* Ads Awareness Section */}
      <>
        <H3>Ads Awareness</H3>
        <P>
          It shows how far your message is spreading and who is seeing it. By analyzing these metrics, you can
          understand the scale of your brand's visibility and fine-tune targeting to ensure your message reaches the
          right audience in the right volume.
        </P>
      </>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <>
            <ChartCardSkeleton title="Impressions" />
            <ChartCardSkeleton title="Reach" />
            <ChartCardSkeleton title="Views per person" />
          </>
        ) : (
          <>
            <AnalyticsCard className="col-span-1">
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>Impressions</AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                  <EmptyChart
                    title="Impressions Data Unavailable"
                    message="Try adjusting your filters or check back once your campaigns has gathered more results."
                    iconComponent={<ChartBarIcon className="w-12 h-12" />}
                  />
                ) : (
                  <ImpressionsChart
                    currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                    currentIntervalsIntervalInsightsNotAspireSourced={currentIntervalsIntervalInsightsNotAspireSourced}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                    precision={currentIntervalRange.precision}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>

            <AnalyticsCard className="col-span-1">
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>Reach</AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                  <EmptyChart
                    title="Reach Data Unavailable"
                    message="Try adjusting your filters or check back once your campaigns has gathered more results."
                    iconComponent={<ChartBarIcon className="w-12 h-12" />}
                  />
                ) : (
                  <ReachChart
                    currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                    currentIntervalsIntervalInsightsNotAspireSourced={currentIntervalsIntervalInsightsNotAspireSourced}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                    precision={currentIntervalRange.precision}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>

            <AnalyticsCard className="col-span-1">
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>Views per person</AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                  <EmptyChart
                    title="Views Data Unavailable"
                    message="Try adjusting your filters or check back once your campaigns has gathered more results."
                    iconComponent={<ChartBarIcon className="w-12 h-12" />}
                  />
                ) : (
                  <ClickThroughRateChart
                    currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                    currentIntervalsIntervalInsightsNotAspireSourced={currentIntervalsIntervalInsightsNotAspireSourced}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                    precision={currentIntervalRange.precision}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>
          </>
        )}
      </div>

      {/* Metric Cards with Radials Section */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <MetricWithBarSkeleton title="CPC" />
            <MetricWithBarSkeleton title="CPM" />
            <MetricWithBarSkeleton title="CPP" />
            <MetricWithBarSkeleton title="CTR" />
          </>
        ) : (
          <>
            <AnalyticsCard>
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>CPC</AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isMetricDataMissing(currentInterval, 'totalCpc') ? (
                  <EmptyMetricWithBar
                    valueType="currency"
                    currency={currency}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                ) : (
                  <MetricWithBar
                    metricComponent={
                      <Cpc
                        currentInterval={currentInterval}
                        previousInterval={previousInterval}
                        currentIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                        }
                        previousIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                        }
                        showNonAttributedData={queryVariables.showNonAttributedData}
                        currency={currency}
                      />
                    }
                    barComponent={
                      <CpcBar
                        currentInterval={currentInterval}
                        currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                        showNonAttributedData={queryVariables.showNonAttributedData}
                        currency={currency}
                      />
                    }
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>
            <AnalyticsCard>
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>CPM</AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isMetricDataMissing(currentInterval, 'totalCpm') ? (
                  <EmptyMetricWithBar
                    valueType="currency"
                    currency={currency}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                ) : (
                  <MetricWithBar
                    metricComponent={
                      <Cpm
                        currentInterval={currentInterval}
                        previousInterval={previousInterval}
                        currentIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                        }
                        previousIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                        }
                        showNonAttributedData={queryVariables.showNonAttributedData}
                        currency={currency}
                      />
                    }
                    barComponent={
                      <CpmBar
                        currentInterval={currentInterval}
                        currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                        showNonAttributedData={queryVariables.showNonAttributedData}
                        currency={currency}
                      />
                    }
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>
            <AnalyticsCard>
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>CPP</AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isMetricDataMissing(currentInterval, 'totalCpp') ? (
                  <EmptyMetricWithBar
                    valueType="currency"
                    currency={currency}
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                ) : (
                  <MetricWithBar
                    metricComponent={
                      <Cpp
                        currentInterval={currentInterval}
                        previousInterval={previousInterval}
                        currentIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                        }
                        previousIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                        }
                        showNonAttributedData={queryVariables.showNonAttributedData}
                        currency={currency}
                      />
                    }
                    barComponent={
                      <CppBar
                        currentInterval={currentInterval}
                        currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                        showNonAttributedData={queryVariables.showNonAttributedData}
                        currency={currency}
                      />
                    }
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>
            <AnalyticsCard>
              <AnalyticsCardHeader>
                <AnalyticsCardTitle>CTR</AnalyticsCardTitle>
              </AnalyticsCardHeader>
              <AnalyticsCardContent>
                {isMetricDataMissing(currentInterval, 'totalClicks') ||
                isMetricDataMissing(currentInterval, 'totalImpressions') ? (
                  <EmptyMetricWithBar
                    valueType="percentage"
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                ) : (
                  <MetricWithBar
                    metricComponent={
                      <Ctr
                        currentInterval={currentInterval}
                        previousInterval={previousInterval}
                        currentIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                        }
                        previousIntervalNotAspireSourced={
                          queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                        }
                        showNonAttributedData={queryVariables.showNonAttributedData}
                      />
                    }
                    barComponent={
                      <CtrBar
                        currentInterval={currentInterval}
                        currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                        showNonAttributedData={queryVariables.showNonAttributedData}
                      />
                    }
                    showNonAttributedData={queryVariables.showNonAttributedData}
                  />
                )}
              </AnalyticsCardContent>
            </AnalyticsCard>
          </>
        )}
      </div>

      {/* Top Performers Section */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <div>
          <H3>Top Performers</H3>
          <P>
            The hall of fame for your ads. It pinpoints the ads and creators that are driving the most success, showing
            you what resonates with your audience. Use this as a blueprint for future campaigns by exploring why these
            ads worked so well and replicate that success in new ads.
          </P>
        </div>
      </div>

      {/* Top Ads section table */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <AnalyticsCard>
          <AnalyticsCardHeader className="mb-0 border-b-0">
            <AnalyticsCardTitle>
              Top Ads
              <CircleInfoIcon className="text-grey-3 w-4 h-4 ml-2" aria-hidden="true" />
            </AnalyticsCardTitle>
          </AnalyticsCardHeader>
          <AnalyticsCardContent className="px-0 pt-0 pb-6">
            <div className="w-full">
              <SocialAdsOverviewTable data={topAds} isLoading={isLoading} isRefreshing={false} hideAdsTableHeader />
            </div>
            <div className="flex justify-start mt-4 px-4">
              <button
                className="text-grey-6 text-[14px] font-medium leading-6 font-inter underline underline-offset-2 px-3 py-1 rounded-lg whitespace-nowrap flex items-center justify-center"
                onClick={() => handleTabChange('top-ads')}
                type="button"
                data-dd-action-name="social-ads-view-more-top-ads"
              >
                View More
              </button>
            </div>
          </AnalyticsCardContent>
        </AnalyticsCard>
      </div>

      {/* Top Social Accounts section */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <AnalyticsCard>
          <AnalyticsCardHeader className="mb-0 border-b-0">
            <AnalyticsCardTitle>
              Top Creators
              <CircleInfoIcon className="text-grey-3 w-4 h-4 ml-2" aria-hidden="true" />
            </AnalyticsCardTitle>
          </AnalyticsCardHeader>
          <AnalyticsCardContent className="px-0 pt-0 pb-6">
            <div className="w-full">
              <TopSocialAccountsTable data={topSocialAccounts} isLoading={isLoading} isRefreshing={false} />
            </div>
            <div className="flex justify-start mt-4 px-4">
              <button
                className="text-grey-6 text-[14px] font-medium leading-6 font-inter underline underline-offset-2 px-3 py-1 rounded-lg whitespace-nowrap flex items-center justify-center"
                onClick={() => handleTabChange('social-accounts')}
                type="button"
                data-dd-action-name="social-ads-view-more-social-accounts"
              >
                View More
              </button>
            </div>
          </AnalyticsCardContent>
        </AnalyticsCard>
      </div>
    </div>
  );
};

export default SocialAdsOverview;
